import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { IReportsRepository } from "../../../domain/repositories/reports-repository";
import { TrafficSourceSummary } from "domain/entities/traffic-source-summary-response";
import { ReportsFilterContext } from "./ReportsFilterProvider";
import { createBlobDownloader } from "../../../utils/files/downloadBlob";
import { checkParamsFromPortfolio } from "helper/filtersHelper";

type TTrafficSourceSummaryProviderProps = {
  requestParamsIndex: number;
  children: React.ReactNode;
  reportsDatasource: IReportsRepository;
  showCards?: boolean;
};

export type TTrafficSourceSummaryState = {
  requestParamsIndex: number;
  trafficSourceSummaryData?: TrafficSourceSummary;
  downloadingReports?: boolean;
  downloadSourceSummaryReport: () => void;
  isLoading: boolean;
};

const initContext: TTrafficSourceSummaryState = {
  requestParamsIndex: 0,
  downloadSourceSummaryReport: () => null,
  isLoading: true,
};
export const TrafficSourceSummaryContext = createContext(initContext);

export const TrafficSourceSummaryProvider = ({
  requestParamsIndex,
  children,
  reportsDatasource,
  showCards = true,
}: TTrafficSourceSummaryProviderProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [trafficSourceSummaryData, setTrafficSourceSummaryData] =
    useState<TrafficSourceSummary>();
  const [downloadingReports, setDownloadingReports] = useState(false);
  const { requestParams, comparisonEnabled } = useContext(ReportsFilterContext);
  const currentRequestParams = requestParams[requestParamsIndex];

  const downloadSourceSummaryReport = useCallback(() => {
    setDownloadingReports(true);
    return reportsDatasource
      .exportTrafficSourceSummaryCSV(currentRequestParams)
      .then(createBlobDownloader("traffic-source-summary.zip"))
      .finally(() => setDownloadingReports(false));
  }, [currentRequestParams]);

  const canShowPanel = useMemo(() => {
    if (comparisonEnabled) {
      return requestParams.every(checkParamsFromPortfolio);
    }

    return checkParamsFromPortfolio(currentRequestParams);
  }, [requestParams]);

  useEffect(() => {
    if (canShowPanel) {
      setIsLoading(true);
      reportsDatasource
        .getTrafficSourceSummary({ ...currentRequestParams, showCards })
        .then(setTrafficSourceSummaryData)
        .then(() => setIsLoading(false));
    }
  }, [currentRequestParams]);

  return (
    <TrafficSourceSummaryContext.Provider
      value={{
        requestParamsIndex,
        trafficSourceSummaryData,
        downloadingReports,
        downloadSourceSummaryReport,
        isLoading,
      }}
    >
      {canShowPanel ? children : <></>}
    </TrafficSourceSummaryContext.Provider>
  );
};
